import { useEffect, useRef, useState } from 'react';
import { C, FONTS } from '../../tokens/design';
import { formatApiError } from '../../api/apiClient';

const SEVERITY_LABELS = ['Info', 'Low', 'Medium', 'High', 'Critical'];

export const CORRECTION_FIELDS = [
  ['title', 'Finding title', 500],
  ['description', 'Description and evidence limitations', 16000],
  ['affectedComponent', 'Affected scope', 8000],
  ['remediation', 'Remediation guidance', 16000],
  ['businessImpact', 'Business impact', 4000],
  ['validationSteps', 'Validation and closure criteria', 4000],
];

export function validateCorrection(fields, reason, originalFields = null) {
  if (!fields || !Number.isInteger(fields.severity) || fields.severity < 0 || fields.severity > 4)
    return 'Select a valid severity.';
  for (const [key, label, max] of CORRECTION_FIELDS) {
    const preservedBlank = (key === 'businessImpact' || key === 'validationSteps') &&
      typeof fields[key] === 'string' && !fields[key].trim() &&
      fields[key] === originalFields?.[key];
    if (typeof fields[key] !== 'string' || (!fields[key].trim() && !preservedBlank) || fields[key].length > max)
      return `${label} requires 1 to ${max} characters.`;
    if (/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/.test(fields[key]))
      return 'Control characters are not permitted.';
  }
  if (!reason.trim() || reason.length > 1000) return 'A correction reason (1-1000 characters) is required.';
  return '';
}

export function canCorrectFindings(profile) {
  return profile?.role === 'PlatformAdmin' || profile?.role === 'MspAdmin' || profile?.role === 'PartnerAdmin' ||
    profile?.isPlatformAdmin === true || (profile?.isMspUser === true && profile?.role === 'Admin');
}

export function correctionPayload(state, fields, reason) {
  return {
    version: state.version,
    fields: Object.fromEntries([['severity', fields.severity], ...CORRECTION_FIELDS.map(([key]) => [key, fields[key]])]),
    reason: reason.trim(),
  };
}

// Mount with a client/finding key. Never carry a draft or late response across tenant navigation.
export default function FindingCorrectionEditor({ api, clientId, findingId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null);
  const [fields, setFields] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [review, setReview] = useState(false);
  const generation = useRef(0);
  useEffect(() => () => { generation.current++; }, [clientId, findingId]);
  const path = `/clients/${clientId}/findings/${findingId}/corrections`;

  async function load() {
    const request = ++generation.current;
    setOpen(true); setBusy(true); setError(''); setReview(false); setState(null); setFields(null); setReason('');
    try {
      const result = await api.get(path);
      if (request !== generation.current) return;
      if (!result?.version || !result?.fields) throw new Error('Editor data is unavailable. Nothing has been changed.');
      setState(result); setFields(result.fields);
    } catch (err) {
      if (request === generation.current) setError(formatApiError(err, 'Unable to load the finding editor.'));
    } finally {
      if (request === generation.current) setBusy(false);
    }
  }

  async function save() {
    const invalid = validateCorrection(fields, reason, state?.fields);
    if (invalid || !review || !state) { setError(invalid || 'Review the changes first.'); return; }
    const request = ++generation.current;
    setBusy(true); setError('');
    try {
      const result = await api.put(path, correctionPayload(state, fields, reason));
      if (request !== generation.current) return;
      if (result?.saved !== true && result?.saved !== false) throw new Error('Save was not confirmed. Reload before retrying.');
      setNotice(result.saved ? 'Correction saved with original evidence, dates and audit history retained.' : 'No field changes were needed.');
      setOpen(false); setState(null); setFields(null); setReason(''); setReview(false);
      onSaved();
    } catch (err) {
      if (request === generation.current) {
        setError(err?.status === 409
          ? 'This finding changed while you were editing. Nothing was overwritten. Reload current finding before retrying.'
          : formatApiError(err, 'Correction failed. No success has been confirmed.'));
        setReview(false);
        if (err?.status === 409) setState(null);
      }
    } finally {
      if (request === generation.current) setBusy(false);
    }
  }

  const inputStyle = { display: 'block', width: '100%', boxSizing: 'border-box', margin: '5px 0 12px',
    background: C.s2, color: C.t1, border: `1px solid ${C.b1}`, borderRadius: 5, padding: 9, fontFamily: FONTS.sans };
  const buttonStyle = { padding: '8px 12px', marginRight: 8, cursor: 'pointer', color: C.t1, background: C.s2,
    border: `1px solid ${C.b1}`, borderRadius: 5 };
  return (
    <section aria-label="Analyst finding corrections" style={{ padding: 16, marginBottom: 16, border: `1px solid ${C.b1}`, borderRadius: 8 }}>
      <button type="button" style={buttonStyle} disabled={busy} onClick={load}>Edit finding / correction history</button>
      {notice && <p role="status">{notice}</p>}
      {open && <>
        <h2>Correct existing finding</h2>
        <p>This changes the current analyst interpretation, not the assessment. Original evidence, collection dates,
          finding identity and lifecycle are read-only. A reason and before/after audit record are required.</p>
        <p>A newer collector observation may replace this interpretation; the correction history remains.
          Existing issued reports are historical and are not rewritten.</p>
        {busy && <p role="status">Working…</p>}
        {error && <p role="alert" style={{ color: C.red }}>{error}</p>}
        {fields && <>
          {CORRECTION_FIELDS.map(([key, label, max]) => <label key={key} style={{ display: 'block' }}>
            {label}
            <textarea aria-label={label} maxLength={max} rows={key === 'title' ? 2 : 4} style={inputStyle}
              value={fields[key] ?? ''} disabled={busy || review}
              onChange={event => { setFields(previous => ({ ...previous, [key]: event.target.value })); setReview(false); }} />
          </label>)}
          <label>Severity
            <select aria-label="Correction severity" style={inputStyle} value={fields.severity} disabled={busy || review}
              onChange={event => { setFields(previous => ({ ...previous, severity: Number(event.target.value) })); setReview(false); }}>
              {SEVERITY_LABELS.map((label, value) => <option key={label} value={value}>{label}</option>)}
            </select>
          </label>
          <label>Correction reason
            <textarea aria-label="Correction reason" style={inputStyle} rows={3} maxLength={1000}
              value={reason} disabled={busy || review} onChange={event => setReason(event.target.value)} />
          </label>
          {review && <div aria-label="Correction change preview">
            <h3>Before / after</h3>
            {['severity', ...CORRECTION_FIELDS.map(([key]) => key)].filter(key => state.fields[key] !== fields[key]).map(key =>
              <div key={key}><strong>{key}</strong>{key === 'severity'
                ? <p>{SEVERITY_LABELS[state.fields.severity]} → {SEVERITY_LABELS[fields.severity]}</p>
                : <><pre style={{ whiteSpace: 'pre-wrap' }}>Before: {String(state.fields[key] ?? '')}</pre>
                  <pre style={{ whiteSpace: 'pre-wrap' }}>After: {String(fields[key] ?? '')}</pre></>}</div>)}
            <p>Reason: {reason}</p>
            <button type="button" style={buttonStyle} disabled={busy} onClick={() => setReview(false)}>Back to editing</button>
            <button type="button" style={buttonStyle} disabled={busy} onClick={save}>Save audited correction</button>
          </div>}
          {!review && <button type="button" style={buttonStyle} disabled={busy || !state} onClick={() => {
            const invalid = validateCorrection(fields, reason, state?.fields); setError(invalid); if (!invalid) setReview(true);
          }}>Review changes</button>}
          <details><summary>Read-only current source snapshot</summary><pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{state?.snapshotJson}</pre></details>
        </>}
        <button type="button" style={buttonStyle} disabled={busy} onClick={load}>Reload current finding</button>
        <button type="button" style={buttonStyle} disabled={busy} onClick={() => { generation.current++; setOpen(false); setState(null); setFields(null); }}>Cancel</button>
        {state?.history?.length > 0 && <div aria-label="Correction audit history"><h3>Correction history (latest 100)</h3>
          {state.history.map(item => <details key={item.revisionId}>
            <summary>{item.changedAt} — {item.changedBy}: {item.reason}</summary>
            <p>Original values immediately before this correction:</p><pre style={{ whiteSpace: 'pre-wrap' }}>{item.beforeJson}</pre>
            <p>Values after this correction:</p><pre style={{ whiteSpace: 'pre-wrap' }}>{item.afterJson}</pre>
          </details>)}
        </div>}
      </>}
    </section>
  );
}
